import { animate, AnimationBuilder, AnimationFactory, AnimationPlayer, style } from "@angular/animations";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnChanges,
	SimpleChange,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { truncate } from "lodash-es";

import { ExpandableTextOptions } from "./expandable-text.model";

const DEFAULT_OPTIONS: ExpandableTextOptions = {
	maxCharacters: 300,
};

@Component({
	selector: "obg-expandable-text",
	templateUrl: "./expandable-text.component.html",
	styleUrls: ["./expandable-text.component.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpandableTextComponent implements OnChanges {
	@Input() readLess: string | undefined;
	@Input() readMore: string | undefined;
	@Input() text: string | undefined;
	@Input()
	get options(): ExpandableTextOptions {
		return this._options;
	}
	set options(value: ExpandableTextOptions) {
		this._options = { ...DEFAULT_OPTIONS, ...value };
	}

	@ViewChild("content", { static: true }) content: ElementRef | undefined;

	isExpanded = false;
	isCollapsable = false;
	textToShow: string | undefined;

	private _options = DEFAULT_OPTIONS;

	private collapseAnimation: AnimationFactory | undefined;
	private expandAnimation: AnimationFactory | undefined;

	constructor(
		private changeDetector: ChangeDetectorRef,
		private animationBuilder: AnimationBuilder
	) {}

	ngOnChanges(changes: { text?: SimpleChange }) {
		if (changes.text) {
			this.truncateText();
		}
	}

	toggle() {
		this.isExpanded = !this.isExpanded;
		this.animate();
	}

	private truncateText() {
		if (!this.text || this.text.length > this.options.maxCharacters) {
			this.textToShow = truncate(this.text, { length: this.options.maxCharacters });
			this.isCollapsable = true;
		} else {
			this.textToShow = this.text;
		}

		this.changeDetector.markForCheck();
	}

	private animate() {
		if (!this.content) {
			return;
		}
		if (!this.collapseAnimation) {
			this.collapseAnimation = this.animationBuilder.build([
				animate(
					"200ms ease-out",
					style({
						height: `${this.content.nativeElement.scrollHeight}px`,
					})
				),
			]);
		}

		if (!this.expandAnimation) {
			this.expandAnimation = this.animationBuilder.build([
				animate(
					"200ms ease-in",
					style({
						height: "100%",
					})
				),
			]);
		}

		let animation: AnimationPlayer;

		if (this.isExpanded) {
			animation = this.expandAnimation.create(this.content.nativeElement);
			animation.onStart(() => (this.textToShow = this.text));
		} else {
			animation = this.collapseAnimation.create(this.content.nativeElement);
			animation.onDone(() => this.truncateText());
		}

		animation.play();
	}
}
